import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl, FormControlLabel, FormLabel,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Radio, RadioGroup,
    TextField,
    Typography,
    useTheme
} from "@mui/material";
import React, {
    forwardRef, ForwardRefRenderFunction,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from "react";
import colorTheme from "../../../assets/colorTheme";
import { DtAutocomplete } from "../autocomplete/DtAutocomplete";
import { DtLabel } from "../label";
import { FormField } from "./formTypes";


interface DtFormProps {
    fields: Array<FormField>;
    onSubmit: (data: Record<string, any>) => void;
    gap?: number | string;
    labelPosition?: "inline" | "ontop" | "incontrol";
    showSubmit?: boolean;
    showReset?: boolean;
    mode?: "interactive" | "view";
    values?: Record<string, any>; // <-- add this line
    responsiveTextareaRows?: boolean; // <-- add this line
}

export interface DtFormHandles {
    submit: () => void;
    reset: () => void;
}

interface DtFormProps {
    fields: Array<FormField>;
    onSubmit: (data: Record<string, any>) => void;
    gap?: number | string;
    labelPosition?: "inline" | "ontop" | "incontrol";
    showSubmit?: boolean;
    showReset?: boolean;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getDefaultValue = (field: FormField) =>
    field.defaultValue !== undefined
        ? field.defaultValue
        : field.type === "checkbox"
            ? false
            : field.type === "radio"
                ? ""
                : "";

const DtForm: ForwardRefRenderFunction<DtFormHandles, DtFormProps> = (
    { fields, onSubmit, gap = 2, labelPosition = "ontop", showSubmit = true, showReset = true, mode = "interactive", values, responsiveTextareaRows = false }, // <-- add values here
    ref
) => {
    const [formState, setFormState] = useState<Record<string, any>>(
        () => fields.reduce((acc, field) => ({ ...acc, [field.id]: getDefaultValue(field) }), {})
    );
    const [passwordVisibility, setPasswordVisibility] = useState<Record<string, boolean>>({});
    const theme = useTheme();

    // Add this effect to update formState when values prop changes
    useEffect(() => {
        if (values) {
            setFormState(prev => {
                // Only update if values actually changed
                const changed = Object.keys(values).some(
                    key => values[key] !== prev[key]
                );
                return changed ? { ...prev, ...values } : prev;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    const [errors, setErrors] = useState<Record<string, string | undefined>>({});
    const prevFormState = useRef(formState);
    const formRef = useRef<HTMLFormElement>(null);

    // Calculate values for fields with calculateValue and dependencies
    useEffect(() => {
        let updated: Record<string, any> = {};
        fields.forEach(field => {
            if (typeof field.calculateValue === "function") {
                if (field.dependencies?.length) {
                    const depChanged = field.dependencies.some(
                        dep => prevFormState.current[dep] !== formState[dep]
                    );
                    if (!depChanged) return;
                }
                const newValue = field.calculateValue(formState);
                if (formState[field.id] !== newValue) updated[field.id] = newValue;
            }
        });
        if (Object.keys(updated).length) setFormState(prev => ({ ...prev, ...updated }));
        prevFormState.current = formState;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formState, fields]);

    const handleFieldChange = (field: FormField, value: any) => {
        setFormState(prev => ({ ...prev, [field.id]: value }));
        field.onChange?.(value, field);
        if (field.validate) {
            setErrors(prev => ({
                ...prev,
                [field.id]: field.validate ? field.validate(value, formState) : undefined
            }));
        }
    };

    const handleReset = () => {
        setFormState(fields.reduce((acc, field) => ({ ...acc, [field.id]: getDefaultValue(field) }), {}));
        setErrors({});
        setPasswordVisibility({});
    };

    const togglePasswordVisibility = (fieldId: string) => {
        setPasswordVisibility(prev => ({
            ...prev,
            [fieldId]: !prev[fieldId]
        }));
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        let hasError = false;
        const newErrors: Record<string, string | undefined> = {};
        fields.forEach(field => {
            if (field.validate) {
                const error = field.validate(formState[field.id], formState);
                newErrors[field.id] = error;
                if (error) hasError = true;
            }
            if (field.required && (
                formState[field.id] === undefined ||
                formState[field.id] === "" ||
                (field.type === "checkbox" && !formState[field.id])
            )) {
                newErrors[field.id] = "This field is required";
                hasError = true;
            }
            if (field.type === "email" && formState[field.id] && !emailRegex.test(formState[field.id])) {
                newErrors[field.id] = "Please enter a valid email address";
                hasError = true;
            }
        });
        setErrors(newErrors);
        if (!hasError) onSubmit(formState);
    };

    useImperativeHandle(ref, () => ({
        submit: () => formRef.current?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true })),
        reset: handleReset
    }));

    // Group fields by row
    const rows: { [row: number]: FormField[] } = {};
    fields.forEach(field => {
        const rowIdx = field.row ?? 0;
        if (!rows[rowIdx]) rows[rowIdx] = [];
        rows[rowIdx].push(field);
    });

    const getFieldValue = (field: FormField) =>
        typeof field.calculateValue === "function"
            ? (() => { try { return field.calculateValue(formState); } catch { return ""; } })()
            : formState[field.id];

    const [screenHeight, setScreenHeight] = useState(window.innerHeight);

    useEffect(() => {
        if (!responsiveTextareaRows) return;
        const handleResize = () => setScreenHeight(window.innerHeight);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [responsiveTextareaRows]);

    const renderTextField = (
        field: FormField,
        value: any,
        handleFieldChange: (field: FormField, value: any) => void,
        helperText: string | undefined,
        error: boolean,
        disabled: boolean,
        label?: string
    ) => {
        const isReadOnly = !!field.readOnly;
        const sxReadOnly = isReadOnly
            ? {
                bgcolor: colorTheme.palette.background.default,
                "& .MuiOutlinedInput-root": {
                    bgcolor: colorTheme.palette.background.default,
                    pointerEvents: "none"
                },
            }
            : undefined;

        // Responsive textarea rows logic
        let minRows = field.textareaRows || 3;
        if (responsiveTextareaRows && field.type === "textarea" && field.textareaRows) {
            const baseRows = field.textareaRows;
            const minWidth = 1024;
            const maxWidth = 1920;
            if (window.innerWidth >= minWidth) {
                const clampedWidth = Math.min(window.innerWidth, maxWidth);
                const extra = Math.floor((clampedWidth - minWidth) / 150);
                minRows = baseRows + extra;
            }
        }

        // Validation result for icon
        let validationResult: string | undefined = undefined;
        if (typeof field.validate === "function") {
            validationResult = field.validate(value, {});
        }
        const showIcon = field.showValidationStatusIcon && value !== undefined && value !== "";

        // Handle password field type
        const isPasswordField = field.type === "password";
        const showPassword = passwordVisibility[field.id] || false;
        const inputType = isPasswordField ? (showPassword ? "text" : "password") : field.type;

        const propsTextField = {
            fullWidth: true,
            size: "small" as const,
            id: field.id,
            variant: "outlined" as const,
            type: inputType,
            value: value ?? "",
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(field, e.target.value),
            helperText,
            error,
            disabled,
            inputProps: {
                ...field.inputProps,
                readOnly: field.readOnly,
            },
            placeholder: field.placeholder,
            label,
            sx: {
                ...sxReadOnly,
                // Hide browser's default password reveal button
                ...(isPasswordField && {
                    // Hide Edge/IE default password reveal button
                    '& input': {
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                    },
                    '& input[type="password"]::-ms-reveal': {
                        display: 'none',
                    },
                    '& input[type="password"]::-ms-clear': {
                        display: 'none',
                    },
                    '& input::-webkit-credentials-auto-fill-button': {
                        display: 'none !important',
                        visibility: 'hidden',
                        pointerEvents: 'none',
                        position: 'absolute',
                        right: '0',
                    },
                })
            },
        };

        if (field.type === "textarea") {
            return (
                <TextField
                    {...propsTextField}
                    multiline
                    minRows={minRows}
                />
            );
        }
        if (field.type === "custom" && typeof field.renderCustomControl === "function") {
            return field.renderCustomControl({
                value,
                onChange: (val: any) => handleFieldChange(field, val),
                field
            });
        }
        // Create end adornment for password toggle and/or status icon
        const createEndAdornment = () => {
            const elements = [];

            // Add password visibility toggle for password fields
            if (isPasswordField && !disabled && !field.readOnly) {
                elements.push(
                    <IconButton
                        key="password-toggle"
                        size="small"
                        onClick={() => togglePasswordVisibility(field.id)}
                        edge="end"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        sx={{ mr: showIcon ? 0.5 : 0 }}
                    >
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                );
            }

            // Add validation status icon
            if (showIcon) {
                elements.push(
                    validationResult
                        ? <ErrorIcon key="error-icon" fontSize="small" sx={{ color: "#C33E38" }} />
                        : <CheckCircleIcon key="success-icon" fontSize="small" sx={{ color: "#4AB15F" }} />
                );
            }

            return elements.length > 0 ? (
                <InputAdornment position="end">
                    {elements}
                </InputAdornment>
            ) : null;
        };

        // Default text field with password toggle and status icon
        return (
            <TextField
                {...propsTextField}
                InputProps={{
                    endAdornment: createEndAdornment()
                }}
            />
        );
    };

    // Render a single field control
    const renderFieldControl = (
        field: FormField,
        value: any,
        options: Array<{ label: string; value: string }>,
        helperText: string | undefined,
        disabled: boolean,
        labelInControl: boolean
    ) => {
        switch (field.type) {
            case "text":
            case "number":
            case "email":
            case "password":
            case "textarea":
            case "custom":
                return renderTextField(
                    field,
                    value,
                    handleFieldChange,
                    helperText,
                    !!errors[field.id],
                    disabled,
                    labelInControl ? field.label : undefined
                );
            case "select":
                return (
                    <FormControl fullWidth size="small" variant="outlined">
                        <TextField
                            select
                            id={field.id}
                            label={labelInControl ? field.label : undefined}
                            value={value ?? ""}
                            onChange={e => handleFieldChange(field, e.target.value)}
                            error={!!errors[field.id]}
                            helperText={errors[field.id] || field.helperText}
                            size="small"
                            variant="outlined"
                            disabled={disabled}
                        >
                            {options.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </FormControl>
                );
            case "dropdown":
                return (
                    <DtAutocomplete
                        id={field.id}
                        size="small"
                        options={options}
                        value={options.find(opt => opt.value === value) || undefined}
                        onChange={(_, val) => handleFieldChange(field, val ? val.value : "")}
                        getOptionLabel={option => option.label}
                        renderInput={params => <TextField {...params} label={labelInControl ? field.label : undefined} />}
                    />
                );
            case "checkbox":
                return (
                    <FormControlLabel
                        control={
                            <Checkbox
                                id={field.id}
                                checked={!!value}
                                onChange={e => handleFieldChange(field, e.target.checked)}
                                disabled={disabled}
                                inputProps={field.inputProps}
                            />
                        }
                        label={field.label}
                    />
                );
            case "radio":
                return (
                    <>
                        {labelInControl && <FormLabel>{field.label}</FormLabel>}
                        <RadioGroup
                            id={field.id}
                            row={field.controlDirection === "row"}
                            value={value ?? ""}
                            onChange={e => handleFieldChange(field, e.target.value)}
                        >
                            {options.map(option => (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={<Radio disabled={disabled} />}
                                    label={option.label}
                                />
                            ))}
                        </RadioGroup>
                        {helperText && (
                            <Typography variant="caption" color={errors[field.id] ? "error" : "text.secondary"}>
                                {helperText}
                            </Typography>
                        )}
                    </>
                );
            default:
                return null;
        }
    };

    // Group fields by formGroup
    const groupedFields = fields.reduce((acc, field) => {
        const group = field.formGroup || "default";
        if (!acc[group]) acc[group] = [];
        acc[group].push(field);
        return acc;
    }, {} as Record<string, FormField[]>);

    return (
        <form ref={formRef} onSubmit={handleSubmit}>
            <Box>
                {Object.entries(groupedFields).map(([groupName, groupFields], idx) => (
                    <React.Fragment key={groupName}>
                        {groupName !== "default" && (
                            <>
                                <Typography variant="subtitle1" fontWeight={500} sx={{ mt: idx === 0 ? 0 : 3, mb: 1 }}>
                                    {groupName}
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </>
                        )}
                        <Grid container spacing={gap}>
                            {groupFields.map(field => {
                                const options =
                                    typeof field.options === "function"
                                        ? field.options(formState)
                                        : field.options || [];
                                if (field.visibility === false) return null;
                                if (field.shouldRender && !field.shouldRender(formState)) return null;
                                const disabled =
                                    typeof field.disabled === "function"
                                        ? field.disabled(formState)
                                        : !!field.disabled;
                                const labelOnTop = labelPosition === "ontop";
                                const labelInline = labelPosition === "inline";
                                const value = getFieldValue(field);
                                const helperText = errors[field.id] || field.helperText;
                                const labelInControl = labelPosition === "incontrol";

                                return (
                                    <Grid
                                        item
                                        key={field.id}
                                        xs={field.size || field.grid?.xs || 12}
                                        sm={field.grid?.sm}
                                        md={field.grid?.md}
                                        lg={field.grid?.lg}
                                        xl={field.grid?.xl}
                                    >
                                        {labelInline ? (
                                            <Grid container alignItems="start" spacing={1}>
                                                <Grid item xs={4}>
                                                    <DtLabel sx={{ minWidth: 80 }}>{field.label}</DtLabel>
                                                </Grid>
                                                <Grid item xs={8}>
                                                    {renderFieldControl(field, value, options, helperText, disabled, false)}
                                                </Grid>
                                            </Grid>
                                        ) : labelOnTop ? (
                                            <FormControl fullWidth>
                                                <DtLabel sx={{ mb: 1 }}>{field.label}</DtLabel>
                                                {renderFieldControl(field, value, options, helperText, disabled, false)}
                                            </FormControl>
                                        ) : (
                                            <FormControl fullWidth>
                                                {renderFieldControl(field, value, options, helperText, disabled, labelInControl)}
                                            </FormControl>
                                        )}
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </React.Fragment>
                ))}
            </Box>
            {(showSubmit || showReset) && (
                <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
                    {showSubmit && (
                        <Button variant="contained" type="submit">Submit</Button>
                    )}
                    {showReset && (
                        <Button variant="outlined" type="button" onClick={handleReset}>Reset</Button>
                    )}
                </Box>
            )}
        </form>
    );
};

export default forwardRef(DtForm);